// Ballpark Game Day: one game's search activity in the Ballpark app. Same sections, order and grids as
// before, styled like the rest of the dashboard (status bar, panels, headline cards, tables, chart theme).
// Live games show live alerts, predicted surges and staffing; past games show the game's alerts.
// Each section only shows when the backend sends its data.
import React, { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { AlertCircle, AlertTriangle, Bell, Clock, MapPin, X, Zap, Timer, ShoppingBag, Utensils, DoorOpen, TrendingUp, TrendingDown } from 'lucide-react';
import moment from 'moment';
import Icon from '../../../components/atoms/Icon';
import { Panel, PanelHeader } from '../../Concession/components/ConcessionUI';
import { KpiCard } from '../../Concession/live/LiveComponents';
import { CHART_THEME } from '../../Concession/charts/chartTheme';
import '../../Concession/concession.css';
import './ballpark.css';

// Known type config — matches BallparkOverview
const SEARCH_TYPE_CONFIG = {
  concession: { label: 'Concessions', icon: Utensils, color: '#10b981' },
  retail: { label: 'Retail', icon: ShoppingBag, color: '#3b82f6' },
  gate: { label: 'Gates', icon: DoorOpen, color: '#f59e0b' },
};
const UNMATCHED_COLOR = '#dc2626';

// Chart colors (no brand teal on data), the same as the Overview
const SESSIONS_COLOR = '#3e58c0';
const PER_SESSION_COLOR = '#9792ec';
const DURATION_COLOR = '#ec4899';
// Demand categories and search term trend lines
const PALETTE = ['#3e58c0', '#e88559', '#9792ec', '#aa4851', '#76ba92', '#866263'];

// Shared recharts styling, matching the dashboard's chart theme (same as the Overview)
const axis = { stroke: CHART_THEME.text, fontSize: 12, tickLine: false, axisLine: false, fontFamily: CHART_THEME.font };
const grid = { strokeDasharray: '0', stroke: CHART_THEME.grid, vertical: false };
const tooltip = {
  contentStyle: {
    background: 'rgba(26, 26, 26, 0.92)', border: 'none', borderRadius: 10, padding: '8px 12px', fontFamily: CHART_THEME.font,
  },
  labelStyle: { color: '#ffffff', fontWeight: 600, marginBottom: 2 },
  itemStyle: { color: '#ffffff' },
  cursor: { fill: 'rgba(16, 24, 40, 0.04)' },
};

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '0');

// Type tag ("No match" when the term matched no location)
const TypeTag = ({ type, unmatched = false }) => {
  const config = SEARCH_TYPE_CONFIG[type];
  if (!config && !unmatched) return null;
  return (
    <span className="bp-tag">
      <span className="bp-tag-dot" style={{ background: config ? config.color : UNMATCHED_COLOR }} />
      {config ? config.label : 'No match'}
    </span>
  );
};

// Alert card: live alerts can be dismissed; past alerts can't
const SEVERITY = {
  high: { tone: 'bad', icon: AlertCircle },
  medium: { tone: 'warn', icon: AlertTriangle },
  low: { tone: 'good', icon: Bell },
};
const AlertCard = ({ alert, onDismiss }) => {
  const s = SEVERITY[alert.severity] || SEVERITY.low;
  const SevIcon = s.icon;
  return (
    <div className={`bp-alert bp-alert-${s.tone}`}>
      <span className="bp-alert-icon"><SevIcon size={18} /></span>
      <div className="bp-alert-body">
        <div className="bp-alert-head">
          <span className="bp-alert-title">{alert.title}</span>
          {alert.metric && <span className="bp-alert-metric">{alert.metric}</span>}
          {alert.searchType && <TypeTag type={alert.searchType} />}
        </div>
        {alert.message && <p className="bp-alert-text">{alert.message}</p>}
      </div>
      {onDismiss && (
        <button type="button" className="bp-alert-close" aria-label="Dismiss" onClick={() => onDismiss(alert.id)}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

// Predicted surge (live)
const SurgeCard = ({ surge }) => (
  <div className="bp-card">
    <div className="bp-card-head">
      <span className="bp-card-title"><Clock size={16} />{surge.time}</span>
      <span className="bp-pill">{surge.confidence}% confidence</span>
    </div>
    <div className="bp-card-line">
      <strong>{surge.category} surge</strong>
      <TypeTag type={surge.type} />
    </div>
    <p className="bp-card-text">{surge.reason}</p>
    <div className="bp-card-action"><Zap size={15} />{surge.recommendation}</div>
  </div>
);

// Staffing recommendation (live)
const DEMAND_TONE = { high: 'bad', low: 'good' };
const StaffingCard = ({ rec }) => (
  <div className="bp-card">
    <div className="bp-card-head">
      <span className="bp-card-title"><MapPin size={16} />{rec.location}<TypeTag type={rec.type} /></span>
      <span className={`bp-pill bp-pill-${DEMAND_TONE[rec.currentDemand] || 'neutral'}`}>{rec.currentDemand}</span>
    </div>
    <div className="bp-card-action">{rec.recommendation}</div>
    <p className="bp-card-text">{rec.reason}</p>
  </div>
);

// One search type: searches, share, top terms, session length
const SearchTypeCard = ({ typeKey, data }) => {
  const config = SEARCH_TYPE_CONFIG[typeKey];
  if (!config) return null;
  const { label, icon: TypeIcon, color } = config;
  return (
    <div className="bp-type" style={{ '--bp-type': color }}>
      <div className="bp-type-head">
        <span className="bp-type-icon"><TypeIcon size={18} /></span>
        <span className="bp-type-name">{label}</span>
        <span className="bp-type-share">{data.percentage || 0}%</span>
      </div>
      <div className="bp-type-value">
        {fmt(data.count || 0)}
        <span>searches</span>
      </div>
      <div className="bp-type-bar"><i style={{ width: `${Math.min(100, data.percentage || 0)}%` }} /></div>
      {data.topTerms && data.topTerms.length > 0 && (
        <div className="bp-type-terms">
          <span className="bp-mini-label">Top terms</span>
          <div className="bp-chip-row">
            {data.topTerms.slice(0, 5).map((term, idx) => <span key={idx} className="bp-chip">{term}</span>)}
          </div>
        </div>
      )}
      {data.avgSessionDuration && (
        <div className="bp-type-foot">
          <Timer size={15} />
          Avg session <strong>{data.avgSessionDuration}</strong>
        </div>
      )}
    </div>
  );
};

// Ranked terms table
const TermsTable = ({ items, showType, maxHeight }) => (
  <div className="bp-table-wrap" style={{ maxHeight }}>
    <table className="bp-table">
      <thead>
        <tr>
          <th className="bp-rank">#</th>
          <th>Term</th>
          <th className="bp-num">{showType ? 'Count' : 'Searches'}</th>
          {showType && <th className="bp-num">Type</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr key={item.term}>
            <td className="bp-rank">{idx + 1}</td>
            <td className="bp-term">{item.term}</td>
            <td className="bp-num">{fmt(item.count)}</td>
            {showType && <td className="bp-num"><TypeTag type={item.type} unmatched={!SEARCH_TYPE_CONFIG[item.type]} /></td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Only label times on the hour ("7:00 PM") so the axis stays readable
const HourTick = ({ x, y, payload }) => {
  if (!String(payload.value).includes(':00')) return <g />;
  return (
    <text x={x} y={y + 14} textAnchor="middle" fill={CHART_THEME.text} fontSize={11} fontFamily={CHART_THEME.font}>
      {payload.value}
    </text>
  );
};

const BallparkGameView = ({
  currentGame,
  gameMetrics,
  topSearchTerms,
  sessionsOverTime,
  searchDistribution,
  searchTermsOverTime,
  isToday,
  noResultsSearches,
  predictedSurges,
  staffingRecommendations,
  activeAlerts,
  alertHistory,
  dismissAlert,
  demandByCategory,
  searchTypeBreakdown,
  sessionDurationDistribution,
}) => {
  // Derive top terms dynamically from searchTermsOverTime data
  const topTerms = useMemo(() => {
    if (!searchTermsOverTime || searchTermsOverTime.length === 0) return [];
    const firstEntry = searchTermsOverTime[0];
    return Object.keys(firstEntry).filter(k => k !== 'time' && k !== 'timestamp');
  }, [searchTermsOverTime]);

  // Filter to only known search types that have data
  const activeSearchTypes = Object.keys(searchTypeBreakdown || {})
    .filter(key => SEARCH_TYPE_CONFIG[key] && searchTypeBreakdown[key]?.count > 0);

  // No game selected state
  if (!currentGame) {
    return (
      <div className="bp-view">
        <div className="cx-empty-state">
          <Icon name="calendar-event" />
          <div className="cx-empty-title">Select a game</div>
          <div>Choose a game from the selector above to see how fans used Ballpark during it.</div>
        </div>
      </div>
    );
  }

  const when = isToday ? 'Today so far' : 'This game';

  return (
    <div className="bp-view cx-live">
      {/* Game status: live or final, and the date */}
      <div className="cx-live-status bp-status">
        <div className="cx-live-status-left">
          {isToday
            ? <span className="cx-phase">Live</span>
            : <span className="cx-phase cx-phase-final">Final</span>}
          <span className="cx-live-status-detail">{moment(currentGame.date).format('dddd, MMMM D, YYYY')}</span>
        </div>
        {isToday && (
          <div className="cx-live-status-right">
            <span className="cx-live-status-meta">Auto-refreshes every 60s</span>
          </div>
        )}
      </div>

      {/* REAL-TIME ALERTS (Live games only) */}
      {isToday && (activeAlerts || []).length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Live alerts" subtitle="Real-time anomalies and trends detected." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {(activeAlerts || []).map(alert => (
              <AlertCard key={alert.id} alert={alert} onDismiss={dismissAlert} />
            ))}
          </div>
        </Panel>
      )}

      {/* ALERT HISTORY (Historical games) */}
      {!isToday && (alertHistory || []).length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Game alerts summary" subtitle="Notable events during this game." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {(alertHistory || []).map(alert => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        </Panel>
      )}

      {/* KPI Cards */}
      <section className="bp-section">
        <div className="cx-kpi-grid cx-kpi-grid-auto bp-kpis">
          <KpiCard label="Sessions" info="Times fans opened Ballpark." value={fmt(gameMetrics?.totalSessions || 0)}>
            <div className="cx-kpi-foot">{when}</div>
          </KpiCard>
          <KpiCard label="Total searches" info="Searches fans made in Ballpark." value={fmt(gameMetrics?.totalSearches || 0)}>
            <div className="cx-kpi-foot">{when}</div>
          </KpiCard>
          <KpiCard label="Avg searches / session" info="Average number of searches in a session." value={(gameMetrics?.avgSearchesPerSession || 0).toFixed(1)}>
            <div className="cx-kpi-foot">Per session</div>
          </KpiCard>
          <KpiCard label="Active users" info="Different fans who used Ballpark." value={fmt(gameMetrics?.uniqueUsers || 0)}>
            <div className="cx-kpi-foot">{when}</div>
          </KpiCard>
          <KpiCard label="Avg session length" info="Average time a fan spent in a Ballpark session." value={gameMetrics?.avgSessionDurationFormatted || '0:00'}>
            <div className="cx-kpi-foot">{when}</div>
          </KpiCard>
        </div>
      </section>

      {/* SEARCH TYPE BREAKDOWN — dynamic */}
      {activeSearchTypes.length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Search type breakdown" subtitle="Search activity by location type for this game." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {activeSearchTypes.map(typeKey => (
              <SearchTypeCard key={typeKey} typeKey={typeKey} data={searchTypeBreakdown[typeKey]} />
            ))}

            {/* Session Duration Distribution */}
            {(sessionDurationDistribution || []).length > 0 && (
              <div className="bp-type" style={{ '--bp-type': DURATION_COLOR }}>
                <div className="bp-type-head">
                  <span className="bp-type-icon"><Clock size={18} /></span>
                  <span className="bp-type-name">Session duration</span>
                </div>
                <div style={{ height: '160px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sessionDurationDistribution || []} layout="vertical" margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} vertical horizontal={false} />
                      <XAxis type="number" {...axis} fontSize={11} />
                      <YAxis dataKey="duration" type="category" {...axis} fontSize={11} width={60} interval={0} />
                      <Tooltip {...tooltip} formatter={(value) => [`${value} sessions`, 'Count']} />
                      <Bar dataKey="count" fill={DURATION_COLOR} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* PREDICTED SURGES & STAFFING (Live games) */}
      {isToday && ((predictedSurges || []).length > 0 || (staffingRecommendations || []).length > 0) && (
        // Side by side at the same height
        <div className="bp-grid bp-grid-equal" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
          {/* Predicted Surges */}
          {(predictedSurges || []).length > 0 && (
            <Panel className="bp-section">
              <PanelHeader title="Predicted surges" subtitle="Upcoming demand spikes based on patterns." />
              <div className="bp-stack">
                {(predictedSurges || []).map((surge, idx) => (
                  <SurgeCard key={idx} surge={surge} />
                ))}
              </div>
            </Panel>
          )}

          {/* Staffing Recommendations */}
          {(staffingRecommendations || []).length > 0 && (
            <Panel className="bp-section">
              <PanelHeader title="Staffing recommendations" subtitle="Real-time staffing adjustments." />
              <div className="bp-stack">
                {(staffingRecommendations || []).map((rec, idx) => (
                  <StaffingCard key={idx} rec={rec} />
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* DEMAND BY CATEGORY */}
      {(demandByCategory || []).length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Demand by category" subtitle="Search activity broken down by category." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {(demandByCategory || []).map((cat, idx) => {
              const color = PALETTE[idx % PALETTE.length];
              return (
                <div key={cat.category} className="bp-type" style={{ '--bp-type': color }}>
                  <div className="bp-type-head">
                    <span className="bp-type-name">{cat.category}</span>
                    <TypeTag type={cat.type} />
                    <span className="bp-type-share">{cat.percentage}%</span>
                  </div>

                  <div className="bp-type-stats">
                    <div>
                      <span className="bp-mini-label">Searches</span>
                      <strong>{cat.searches?.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="bp-mini-label">Peak time</span>
                      <strong>{cat.peakTime}</strong>
                    </div>
                  </div>

                  {cat.timeline && (
                    <div style={{ height: '100px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={cat.timeline}>
                          <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2} fill={color} fillOpacity={0.12} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {cat.currentVsAvg !== undefined && (
                    <div className={`bp-vs ${cat.currentVsAvg >= 0 ? 'up' : 'down'}`}>
                      {cat.currentVsAvg >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
                      {cat.currentVsAvg >= 0 ? '+' : ''}{cat.currentVsAvg}% vs average
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* DEMAND: unmatched searches and the share of this game's searches by type (same as the Overview) */}
      {((noResultsSearches || []).length > 0 || activeSearchTypes.length > 0) && (() => {
        const unmatched = noResultsSearches || [];
        const unmatchedCount = unmatched.reduce((sum, item) => sum + item.count, 0);
        const classifiedTotal = activeSearchTypes.reduce((sum, key) => sum + (searchTypeBreakdown[key]?.count || 0), 0);
        const grandTotal = classifiedTotal + unmatchedCount;
        const pieData = [
          ...activeSearchTypes.map(typeKey => ({
            name: SEARCH_TYPE_CONFIG[typeKey].label,
            value: searchTypeBreakdown[typeKey].count,
            percentage: grandTotal > 0 ? Math.round(searchTypeBreakdown[typeKey].count / grandTotal * 1000) / 10 : 0,
            color: SEARCH_TYPE_CONFIG[typeKey].color,
          })),
          ...(unmatchedCount > 0 ? [{
            name: 'Unmatched',
            value: unmatchedCount,
            percentage: grandTotal > 0 ? Math.round(unmatchedCount / grandTotal * 1000) / 10 : 0,
            color: UNMATCHED_COLOR,
          }] : []),
        ];

        return (
          <Panel className="bp-section">
            <PanelHeader
              title={isToday ? 'Live demand' : 'Demand'}
              subtitle={`What fans searched for by location type, and the searches that matched no location, ${isToday ? 'today so far' : 'during this game'}.`}
            />
            {/* Both sides stretch to the taller one */}
            <div className="bp-grid bp-grid-equal" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
              {unmatched.length > 0 && (
                <div className="bp-fill">
                  <div className="bp-sub-title">Top unmatched searches</div>
                  <p className="bp-sub-text">Searches that returned no results during this game.</p>
                  <TermsTable items={unmatched} maxHeight={360} />
                </div>
              )}

              {pieData.length > 0 && (
                <div className="bp-inset">
                  <div className="bp-sub-title">Demand by category</div>
                  <p className="bp-sub-text">Share of this game's searches, including the ones that matched nothing.</p>
                  <div className="bp-donut">
                    <div className="bp-donut-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius="100%"
                            innerRadius="66%"
                            paddingAngle={2}
                            stroke="#ffffff"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip {...tooltip} formatter={(value, name) => [value.toLocaleString() + ' searches', name]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="bp-donut-center">
                        <strong>{fmt(grandTotal)}</strong>
                        <span>searches</span>
                      </div>
                    </div>
                    <ul className="bp-donut-legend">
                      {pieData.map((d) => (
                        <li key={d.name}>
                          <span className="cx-ov-swatch" style={{ background: d.color }} />
                          <span className="bp-donut-name">{d.name}</span>
                          <span className="bp-donut-val">{fmt(d.value)}</span>
                          <span className="bp-donut-share">{d.percentage}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </Panel>
        );
      })()}

      {/* TOP SEARCH TERMS: when the top terms were searched, beside the ranked list */}
      {((topSearchTerms || []).length > 0 || (topTerms.length > 0 && (searchTermsOverTime || []).length > 0)) && (() => {
        const hasTerms = (topSearchTerms || []).length > 0;
        const hasTrends = topTerms.length > 0 && (searchTermsOverTime || []).length > 0;
        return (
          <Panel className="bp-section">
            <PanelHeader title="Top search terms" subtitle="What fans searched for most during this game, and when the top terms peaked." />
            {/* Both sides stretch to the taller one */}
            <div className="bp-grid bp-grid-equal" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
              {hasTrends && (
                <div className="bp-terms-left">
                  <div className="bp-trends-head">
                    <span className="bp-sub-title">Search term trends</span>
                    <span className="bp-legend">
                      {topTerms.map((term, idx) => (
                        <span key={term} className="bp-legend-item">
                          <span className="bp-legend-line" style={{ background: PALETTE[idx % PALETTE.length] }} />
                          {term}
                        </span>
                      ))}
                    </span>
                  </div>
                  <p className="bp-sub-text">When the top terms were searched through the game.</p>
                  <div style={{ height: '320px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={searchTermsOverTime || []} margin={{ left: 0, right: 8, top: 4 }}>
                        <CartesianGrid {...grid} />
                        <XAxis dataKey="time" {...axis} fontSize={11} interval={0} tick={<HourTick />} height={24} />
                        <YAxis {...axis} width={36} />
                        <Tooltip {...tooltip} cursor={{ stroke: CHART_THEME.grid }} />
                        {topTerms.map((term, idx) => (
                          <Line key={term} type="monotone" dataKey={term} stroke={PALETTE[idx % PALETTE.length]} strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {hasTerms && (
                <div className={`bp-fill${hasTrends ? ' bp-fill-match' : ''}`}>
                  <div className="bp-sub-title">Search terms detail</div>
                  <p className="bp-sub-text">Every term, most searched first, with the kind of location it matched.</p>
                  <TermsTable items={topSearchTerms || []} showType maxHeight={hasTrends ? undefined : 360} />
                </div>
              )}
            </div>
          </Panel>
        );
      })()}

      {/* ACTIVITY */}
      {((sessionsOverTime || []).length > 0 || (searchDistribution || []).length > 0) && (
        <Panel className="bp-section">
          <PanelHeader title="Activity" subtitle="When fans opened Ballpark during the game and how much they searched." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
            {(sessionsOverTime || []).length > 0 && (
              <div>
                <div className="bp-sub-title">Sessions throughout the game</div>
                <p className="bp-sub-text">When fans are checking queues.</p>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sessionsOverTime || []} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} />
                      <XAxis dataKey="time" {...axis} height={30} interval={0} tick={<HourTick />} />
                      <YAxis {...axis} />
                      <Tooltip {...tooltip} cursor={{ stroke: CHART_THEME.grid }} formatter={(value) => [`${value.toLocaleString()} sessions`, 'Sessions']} />
                      <Line type="monotone" dataKey="sessions" stroke={SESSIONS_COLOR} strokeWidth={2.25} dot={{ fill: SESSIONS_COLOR, r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {(searchDistribution || []).length > 0 && (
              <div>
                <div className="bp-sub-title">Searches per session</div>
                <p className="bp-sub-text">How many searches fans made in a session.</p>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={searchDistribution || []} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} />
                      <XAxis dataKey="searches" {...axis} />
                      <YAxis {...axis} />
                      <Tooltip {...tooltip} formatter={(value) => [`${value.toLocaleString()} sessions`, 'Sessions']} />
                      <Bar dataKey="count" fill={PER_SESSION_COLOR} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}

    </div>
  );
};

export default BallparkGameView;
